<template>
  <div
    class="empty-container"
    :class="[
      {
        'empty-simple': simple,
        'empty-compact': size === 'small'
      }
    ]"
    :style="containerStyle"
  >
    <!-- 空状态图标/图片 -->
    <div class="empty-image">
      <!-- 自定义图片 -->
      <img
        v-if="image"
        :src="image"
        :alt="imageAlt"
        class="empty-img"
        @error="handleImageError"
      />

      <!-- 自定义图标 -->
      <ElIcon
        v-else-if="icon"
        :size="iconSize"
        class="empty-icon"
      >
        <component :is="icon" />
      </ElIcon>

      <!-- 默认空状态图标 -->
      <div v-else class="empty-default-icon">
        <svg
          viewBox="0 0 64 41"
          :width="iconSize"
          :height="iconSize * 0.64"
          class="empty-svg"
        >
          <g transform="translate(0 1)" fill="none" fill-rule="evenodd">
            <ellipse
              fill="#f5f5f5"
              cx="32"
              cy="33"
              rx="32"
              ry="7"
            />
            <g fill-rule="nonzero" stroke="#d9d9d9">
              <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
              <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa" />
            </g>
          </g>
        </svg>
      </div>

      <!-- 插槽：自定义图像 -->
      <slot name="image"></slot>
    </div>

    <!-- 空状态描述 -->
    <div v-if="!simple" class="empty-description">
      <!-- 主要描述文本 -->
      <div v-if="description || $slots.description" class="empty-text">
        <slot name="description">
          {{ description }}
        </slot>
      </div>

      <!-- 次要描述文本 -->
      <div v-if="subDescription || $slots.subDescription" class="empty-sub-text">
        <slot name="subDescription">
          {{ subDescription }}
        </slot>
      </div>
    </div>

    <!-- 操作按钮区域 -->
    <div v-if="!simple && (showAction || $slots.action)" class="empty-actions">
      <slot name="action">
        <!-- 主要操作按钮 -->
        <ElButton
          v-if="actionText"
          :type="actionType"
          :size="actionSize"
          :icon="actionIcon"
          @click="handleAction"
        >
          {{ actionText }}
        </ElButton>

        <!-- 次要操作按钮 -->
        <ElButton
          v-if="secondaryActionText"
          :size="actionSize"
          @click="handleSecondaryAction"
        >
          {{ secondaryActionText }}
        </ElButton>
      </slot>
    </div>

    <!-- 额外内容 -->
    <div v-if="$slots.extra" class="empty-extra">
      <slot name="extra"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Empty 空状态组件
 * 用于显示空数据、无搜索结果等场景
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed, ref } from 'vue'
import type { Component } from 'vue'
import type { ComponentSize } from 'element-plus'

// 接口定义
interface Props {
  /** 空状态描述文本 */
  description?: string
  /** 次要描述文本 */
  subDescription?: string
  /** 自定义图片地址 */
  image?: string
  /** 图片alt文本 */
  imageAlt?: string
  /** 自定义图标 */
  icon?: Component
  /** 图标大小 */
  iconSize?: number
  /** 是否简洁模式 */
  simple?: boolean
  /** 组件大小 */
  size?: ComponentSize | 'mini'
  /** 是否显示操作按钮 */
  showAction?: boolean
  /** 主要操作按钮文本 */
  actionText?: string
  /** 主要操作按钮类型 */
  actionType?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** 主要操作按钮图标 */
  actionIcon?: Component
  /** 操作按钮大小 */
  actionSize?: ComponentSize
  /** 次要操作按钮文本 */
  secondaryActionText?: string
  /** 容器最小高度 */
  minHeight?: string
  /** 容器宽度 */
  width?: string
  /** 自定义样式 */
  customClass?: string
}

interface Emits {
  /** 主要操作点击事件 */
  'action': []
  /** 次要操作点击事件 */
  'secondary-action': []
  /** 图片加载错误事件 */
  'image-error': [event: Event]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  description: '暂无数据',
  subDescription: '',
  image: '',
  imageAlt: '空状态',
  iconSize: 64,
  simple: false,
  size: 'default',
  showAction: false,
  actionText: '',
  actionType: 'primary',
  actionSize: 'default',
  secondaryActionText: '',
  minHeight: '200px',
  width: '100%',
  customClass: ''
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部状态
const imageError = ref(false)

// 计算属性
const containerStyle = computed(() => {
  const sizeMap = {
    mini: '120px',
    small: '160px',
    default: '200px',
    large: '240px'
  }

  return {
    minHeight: props.size in sizeMap ? sizeMap[props.size as keyof typeof sizeMap] : props.minHeight,
    width: props.width
  }
})

// 方法定义
/**
 * 处理主要操作点击
 */
const handleAction = (): void => {
  emit('action')
}

/**
 * 处理次要操作点击
 */
const handleSecondaryAction = (): void => {
  emit('secondary-action')
}

/**
 * 处理图片加载错误
 */
const handleImageError = (event: Event): void => {
  imageError.value = true
  emit('image-error', event)
}

// 暴露方法
defineExpose({
  resetImageError: () => {
    imageError.value = false
  }
})
</script>

<style scoped>
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
  color: var(--el-text-color-secondary);
}

.empty-compact {
  padding: 16px 8px;
}

.empty-simple {
  padding: 16px;
}

.empty-image {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-img {
  max-width: 100%;
  height: auto;
  object-fit: contain;
}

.empty-icon {
  color: var(--el-color-info-light-3);
}

.empty-default-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-svg {
  fill: var(--el-color-info-light-5);
}

.empty-description {
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  line-height: 1.5;
  color: var(--el-text-color-regular);
  margin-bottom: 8px;
  font-weight: 500;
}

.empty-sub-text {
  font-size: 14px;
  line-height: 1.4;
  color: var(--el-text-color-secondary);
}

.empty-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.empty-extra {
  width: 100%;
}

/* 简洁模式样式 */
.empty-simple .empty-image {
  margin-bottom: 8px;
}

.empty-simple .empty-default-icon {
  opacity: 0.6;
}

/* 紧凑模式样式 */
.empty-compact .empty-image {
  margin-bottom: 8px;
}

.empty-compact .empty-text {
  font-size: 14px;
  margin-bottom: 4px;
}

.empty-compact .empty-sub-text {
  font-size: 12px;
}

.empty-compact .empty-actions {
  margin-bottom: 8px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .empty-container {
    padding: 24px 12px;
  }

  .empty-text {
    font-size: 14px;
  }

  .empty-sub-text {
    font-size: 12px;
  }

  .empty-actions {
    flex-direction: column;
    gap: 8px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .empty-svg {
    fill: var(--el-color-info-dark-2);
  }
}

/* 动画效果 */
.empty-container {
  animation: empty-fade-in 0.3s ease-out;
}

@keyframes empty-fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 悬停效果 */
.empty-actions .el-button:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

/* 无障碍支持 */
.empty-container[aria-hidden="true"] {
  pointer-events: none;
}

/* 打印样式 */
@media print {
  .empty-container {
    break-inside: avoid;
  }

  .empty-actions {
    display: none;
  }
}

/* 高对比度模式 */
@media (prefers-contrast: high) {
  .empty-text {
    color: var(--el-text-color-primary);
    font-weight: 600;
  }

  .empty-icon,
  .empty-svg {
    opacity: 1;
  }
}

/* 减少动画模式 */
@media (prefers-reduced-motion: reduce) {
  .empty-container {
    animation: none;
  }

  .empty-actions .el-button:hover {
    transform: none;
    transition: none;
  }
}
</style>
